<template>
  <nav class="attendance-nav">
    <b-nav justified>
      <b-nav-item v-if="!embed" disabled class="nav-item-logo">
        <img class="logo" :src="require('../../assets/goto-logo.svg')" />
      </b-nav-item>

      <b-nav-item class="nav-item-info" @click="clickItem('info')"
        v-if="webinarInfo"
        :class="{active: activeItem === 'info', labels}">
        <icon kind="info"></icon>
        <label v-if="labels">{{ $t('attendance.nav.info') }}</label>
      </b-nav-item>

      <b-nav-item class="nav-item-chat" v-if="canChatOrAskQuestions"
        @click="clickItem('chat')"
        :class="{active: activeItem === 'chat', labels}">
        <icon kind="chat"></icon>
        <label v-if="labels">{{ $t('attendance.nav.chat') }}</label>
      </b-nav-item>

      <b-nav-item class="nav-item-polls" @click="clickItem('polls')"
        v-if="showPollsItem"
        :class="{active: activeItem === 'polls', labels}">
        <icon kind="polls"></icon>
        <label v-if="labels">{{ $t('attendance.nav.polls') }}</label>
      </b-nav-item>

      <b-nav-item class="nav-item-handouts" v-if="inSession && handoutsFiles.length > 0"
        @click="clickItem('handouts')"
        :class="{active: activeItem === 'handouts', labels}">
        <icon kind="file-txt"></icon>
        <label v-if="labels">{{ $t('attendance.nav.handouts') }}</label>
      </b-nav-item>

      <b-nav-item class="nav-item-divider"
        v-if="canRaiseHand || canHeart">
        <hr />
      </b-nav-item>

      <b-nav-item class="nav-item-hand-raise" v-if="canRaiseHand"
        :class="{labels}"
        @click="toggleHandRaising">
        <icon kind="hand-raise" :style="handRaisingButtonStyle"></icon>
        <label v-if="labels">{{ $t(handRaiseLabel) }}</label>
      </b-nav-item>

      <b-nav-item class="nav-item-heart" v-if="canHeart"
        :class="{labels}"
        @click="clickItem('heart')">
        <icon kind="heart"></icon>
      </b-nav-item>

      <b-nav-item class="nav-item-divider"
        v-if="canUseSettings || canExit">
        <hr />
      </b-nav-item>

      <b-nav-item v-if="canUseSettings" class="nav-item-settings" @click="clickItem('settings')"
        :class="{active: activeItem === 'settings', labels}">
        <icon kind="settings"></icon>
        <label v-if="labels">{{ $t('attendance.nav.settings') }}</label>
      </b-nav-item>

      <b-nav-item v-if="canExit" class="nav-item-exit"
        :class="{labels}"
        @click="clickItem('exit')">
        <icon kind="leave-meeting"></icon>
        <label v-if="labels">{{ $t('attendance.nav.exit') }}</label>
      </b-nav-item>
    </b-nav>
  </nav>
</template>

<script>
import Icon from '@/components/togo/icon';
import { SESSION_STATUS } from '../../store/modules/g2w-session';

const AttendanceNav = {
  name: 'AttendanceNav',

  props: {
    labels: {
      type: Boolean,
      default: false
    },

    activeItem: {
      type: String
    },

    showPollsItem: {
      required: true
    }
  },

  components: {
    Icon
  },

  computed: {
    embed() {
      return this.$store.state.embed;
    },

    webinarInfo() {
      return this.$store.state.g2wWebinar.webinarInfo;
    },

    sessionStatus() {
      return this.$store.state.g2wSession.sessionStatus;
    },

    inSession() {
      return this.$store.getters['g2wSession/inSession'];
    },

    handoutsFiles() {
      return this.$store.state.g2wSession.handouts.files;
    },

    canRaiseHand() {
      return this.inSession && this.$store.state.g2wSession.canRaiseHand;
    },

    handRaised() {
      return this.$store.state.g2wSession && this.$store.state.g2wSession.handRaised;
    },

    handRaiseLabel() {
      return this.handRaised ? 'attendance.nav.lowerHand' : 'attendance.nav.raiseHand';
    },

    handRaisingButtonStyle() {
      if (this.handRaised) {
        return {
          color: 'red' // todo: what color?
        };
      }

      return {
        color: 'grey'
      };
    },

    canHeart() {
      return false;
    },

    canChatOrAskQuestions() {
      return this.inSession
        && (this.$store.getters['g2wSession/chatEnabled']
          || this.$store.getters['g2wSession/questionsEnabled']);
    },

    canUseSettings() {
      return this.inSession
        && !this.$store.getters['g2wWebinar/isSimulive'];
    },

    canExit() {
      const debug = this.$store.state.debugMode
      const waitStart = (this.sessionStatus === SESSION_STATUS.waitStart);

      return !this.embed
        && (this.inSession || (debug && waitStart));
    }
  },

  methods: {
    clickItem(item) {
      this.$emit('click-item', {item});
    },

    toggleHandRaising() {
      this.$telemetryTrack('raise hand', {raising: !this.handRaised});
      this.$store.dispatch('g2wSession/toggleHandRaising');
    }
  }
};

export default AttendanceNav;
</script>

<style lang="sass">
  @import 'src/scss/variables'
  @import 'src/scss/mixins'

  .tooltip
    margin-left: -35px // near the icon
    @include layout-vertical
      display: none

  .attendance-nav
    min-width: 94px
    flex-flow: nowrap

    @include layout-vertical
      border-bottom: 1px solid #cecdcd

    @include layout-horizontal
      border-right: 1px solid #cecdcd
      max-width: 94px

    .nav
      min-height: 100%
      flex-direction: column

      @include layout-vertical
        margin-left: 10%
        margin-right: 10%
        flex-direction: row

        @include media-breakpoint-up(md)
          margin-left: 20%
          margin-right: 20%

        @include media-breakpoint-up(lg)
          margin-left: 30%
          margin-right: 30%

      @include layout-horizontal
        .nav-item:first-child:not(.nav-item-logo)
          margin-top: 24px

      .nav-item
        max-height: 64px
        text-align: center

        @include layout-horizontal
          min-height: 30px
          max-height: 30px
          width: 100%
          margin-top: 10px
          margin-bottom: 10px

          &.labels
            max-height: 50px

        .icon
          display: block

        label
          font-size: 14px
          cursor: pointer
          padding-top: 3px

        &.nav-item-logo
          margin-bottom: 20px
          @include layout-vertical
            display: none
          .logo
            max-width: 45px
            max-height: 45px

        .nav-link.disabled
          cursor: default

        .nav-link
          color: $grey-street
          font-size: 25px
          flex-direction: column
          padding-left: 0
          padding-right: 0
          @include layout-horizontal
            padding-top: 0
            padding-bottom: 0
            margin-top: 2px
            line-height: 0.7
          @include layout-vertical
            display: flex

        // workaround smaller poll icon
        .togo-icon-polls
          font-size: 1.8em
          margin: -10px

        &.active
          @include layout-horizontal
            border-left: 4px solid $blue-dk

          @include layout-vertical
            border-bottom: 4px solid $blue-dk

          label
            color: $black

          .nav-link
            color: $black
            @include layout-horizontal
              margin-left: -4px

        &.nav-item-divider
          width: 100%
          min-height: 0
          flex-grow: unset
          @include layout-vertical
            display: none

          .nav-link
            cursor: default
            padding: 0 .3rem

            hr
              border-top: none
              border-bottom: 1px solid #cecdcd
              margin: 0

        &.nav-item-exit
          margin-top: 30px
          @include layout-vertical
            display: none
</style>
